import { reactive, getCurrentInstance, onMounted } from "vue";
import { useFilters } from "@/composables/filters/useFilters";
import { usePositions } from "@/composables/positions/usePositions";
import { useRoles } from "@/composables/catalogs/useRoles";
import { useHighlightRow } from "@/composables/common/useHighlightRow";
import { nextTick } from "vue";
import {
  getRolesListAvailableForPosition,
  getSingleEmployeeV2,
  recalculateEmployeeReadiness,
  getChatterList
} from "@/api";

let instance = null;

export function useOrganizationPositionsViewState() {
  // Check if we're in Storybook context (window.location includes storybook)
  const isStorybook =
    typeof window !== "undefined" &&
    (window.location?.href?.includes("localhost:6006") ||
      window.location?.href?.includes("storybook") ||
      window.__STORYBOOK_ENV__);

  // Only use singleton in non-Storybook contexts
  if (!isStorybook && instance) return instance;

  const positions = usePositions();
  const { getEmployeeAvatar } = usePositions();
  const filter = useFilters();
  const roles = useRoles();
  const { highlight } = useHighlightRow();
  let app = null;
  let proxy = null;
  try {
    app = getCurrentInstance();
    proxy = app?.proxy;
  } catch (e) {
    // In Storybook or other contexts, getCurrentInstance might not be available
    console.warn("getCurrentInstance not available:", e);
  }

  const state = reactive({
    filters: {
      values: [], // Default: no filters (components can set their own defaults via props)
      searchText: "",
      showFilterDialog: false,
      key: "organizationPositions",
      clearSelection: "",
      _displayValueMap: [], // Map from display values to original filter values for removal
      statusOptions: ["Draft", "Active", "Retired", "Inactive"],
      filterGroups: [
        // {
        //   label: "Occupied",
        //   items: [
        //     { label: "No Filter", value: "No Filter" },
        //     { label: "Occupied", value: "Occupied" },
        //     { label: "Not Occupied", value: "Not Occupied" },
        //   ],
        // },
        // {
        //   label: "Preparing For",
        //   items: [
        //     { label: "No Filter", value: "No Filter" },
        //     { label: "Has Candidates", value: "Has Candidates" },
        //     { label: "Nobody", value: "Nobody" },
        //   ],
        // },
        // {
        //   label: "Timeline",
        //   items: [
        //     { label: "No Filter", value: "No Filter" },
        //     { label: "Recently Activated", value: "Recently Activated" },
        //     { label: "Recently Retired", value: "Recently Retired" },
        //     { label: "Recently Created", value: "Recently Created" },
        //   ],
        // },
        {
          label: "Archived",
          items: [
            { label: "Do not Show", value: "Do not Show" },
            { label: "Show", value: "Show" },
          ],
        },
      ],
      // groupsItems: [
      //   {
      //     name: "To be",
      //     groups: [
      //       {
      //         type: "check",
      //         label: "Status",
      //         items: [
      //           { label: "Draft", value: "Draft" },
      //           { label: "Active", value: "Active" },
      //           { label: "Retired", value: "Retired" },
      //           { label: "Inactive", value: "Inactive" },
      //         ],
      //       },
      //       {
      //         type: "radio",
      //         label: "Occupied",
      //         items: [
      //           { label: "No Filter", value: "No Filter" },
      //           { label: "Occupied", value: "Occupied" },
      //           { label: "Not Occupied", value: "Not Occupied" },
      //         ],
      //       },
      //       {
      //         type: "radio",
      //         label: "Preparing For",
      //         items: [
      //           { label: "No Filter", value: "No Filter" },
      //           { label: "Has Candidates", value: "Has Candidates" },
      //           { label: "Nobody", value: "Nobody" },
      //         ],
      //       },
      //       {
      //         type: "radio",
      //         label: "Timeline",
      //         items: [
      //           { label: "No Filter", value: "No Filter" },
      //           { label: "Recently Activated", value: "Recently Activated" },
      //           { label: "Recently Retired", value: "Recently Retired" },
      //           { label: "Recently Created", value: "Recently Created" },
      //         ],
      //       },
      //       {
      //         type: "radio",
      //         label: "Archived",
      //         items: [
      //           { label: "Do not Show", value: "Do not Show" },
      //           { label: "Show", value: "Show" },
      //         ],
      //       },
      //     ],
      //   },
      // ],
    },
    pagination: {
      page: 1,
      perPage: 10,
    },
    sortField: "position_name",
    sortDescending: false,
    config: {
      hideTopSection: true,
      hideActiveNo: false,
      activeRecordsCount: null,
      actionLabel: "Add Position",
      title: "Positions Organization",
      positionsViewSelection: "Grid",
    },
    // Modal states - same as usePositionViewState
    archiveAction: null,
    addPositionModal: {
      show: false,
    },
    activatePositionModal: {
      show: false,
    },
    deletePositionModal: {
      show: false,
      message: "",
    },
    renamePositionModal: {
      show: false,
    },
    attachRoleModal: {
      show: false,
      roles: [],
      attachedRoles: [],
      selectedRoleId: null,
      page: 1,
      isLoadingMore: false,
      hasMoreData: true,
      currentSearchTerm: "",
      currentPositionId: null,
    },
    relieveAssignment: {
      show: false,
      currentPosition: null,
      assignedEmployee: null,
      type: "",
    },
    archivePositionModal: {
      show: false,
    },
    retirePositionModal: {
      show: false,
    },
    reactivatePositionModal: {
      show: false,
    },
    deactivatePositionModal: {
      show: false,
    },
    clonePositionModal: {
      show: false,
    },
    errorPopup: {
      show: false,
      title: "",
      message: "",
    },
    openChatterModal: {
      show: false,
      chatterData: [],
      totalPositionChatter: 0,
      page: 1,
      positionId: null,
    },
    openDefault: false,
    currentPosition: null,
  });

  // Filter Operations
  const applyFilters = async (filters) => {
    if (!filters || !Array.isArray(filters)) {
      return;
    }

    const newFilters = filters
      .map((item) => {
        // Handle string items (status checkboxes)
        if (typeof item === "string") {
          return item;
        }
        // Handle object items
        if (item && typeof item === "object") {
          // For radio buttons, preserve full object with labelType and value
          if (item.labelType !== null && item.labelType !== undefined) {
            const filterValue =
              item.value !== null && item.value !== undefined
                ? item.value
                : item.label;
            // Don't store "No Filter" values - they're the default and don't need to be tracked
            if (filterValue === "No Filter") {
              return null;
            }
            return {
              labelType: item.labelType,
              value: filterValue,
              label: item.label,
            };
          }
          // For checkboxes, they have label and value - return as string for status
          if (item.value !== null && item.value !== undefined) {
            return item.value;
          }
          // Fallback to label if value is not present
          if (item.label !== null && item.label !== undefined) {
            return item.label;
          }
        }
        return null;
      })
      .filter(Boolean); // Remove null/undefined values

    // Create new array reference to trigger reactivity
    state.filters.values = [...newFilters];

    filter.setFilters({
      key: state.filters.key,
      filters: newFilters,
    });

    state.filters.showFilterDialog = false;
    // Reset to first page when filters change
    state.pagination.page = 1;
  };

  const removeFilters = (removedFilter) => {
    // removedFilter is the value from SearchBar's chipText array
    // SearchBar's filteredChipText maps "Show" to "Show Archived" for display,
    // but the actual chipText still has "Show", so removedFilter will be "Show" if it was "Show Archived"
    // Handle "Show Archived" special case
    const filterToRemove =
      removedFilter === "Show Archived" ? "Show" : removedFilter;

    // Use the display value map to find the exact original filter value
    let originalFilterToRemove = null;

    if (
      state.filters._displayValueMap &&
      Array.isArray(state.filters._displayValueMap)
    ) {
      // Find the original filter value that corresponds to this display value
      // SearchBar might emit either the display value or the original value
      const foundOriginal = state.filters._displayValueMap.find(
        (originalVal) => {
          if (typeof originalVal === "string") {
            // String filter - match directly
            return (
              originalVal === filterToRemove || originalVal === removedFilter
            );
          } else if (originalVal && typeof originalVal === "object") {
            // Object filter - match by display value
            const displayValue = originalVal.value || originalVal.label || "";
            return (
              displayValue === filterToRemove || displayValue === removedFilter
            );
          }
          return false;
        }
      );

      if (foundOriginal) {
        originalFilterToRemove = foundOriginal;
      }
    }

    // Remove the filter from values
    const updatedFilters = state.filters.values.filter((val) => {
      // If we found the exact original filter, use that for comparison
      if (
        originalFilterToRemove !== null &&
        originalFilterToRemove !== undefined
      ) {
        if (
          typeof originalFilterToRemove === "string" &&
          typeof val === "string"
        ) {
          return val !== originalFilterToRemove;
        } else if (
          originalFilterToRemove &&
          typeof originalFilterToRemove === "object" &&
          val &&
          typeof val === "object"
        ) {
          // Deep comparison for objects - match by labelType and value
          const originalLabelType = originalFilterToRemove.labelType;
          const originalValue =
            originalFilterToRemove.value || originalFilterToRemove.label;
          const valLabelType = val.labelType;
          const valValue = val.value || val.label;
          return !(
            originalLabelType === valLabelType && originalValue === valValue
          );
        }
      }

      // Fallback: match by display value
      if (typeof val === "string") {
        // String filter (status) - direct comparison
        return val !== filterToRemove && val !== removedFilter;
      } else if (val && typeof val === "object") {
        // Object filter (radio button) - compare display value
        const filterValue = val.value || val.label || "";
        const filterLabel = val.label || val.value || "";
        return (
          filterValue !== filterToRemove &&
          filterLabel !== filterToRemove &&
          filterValue !== removedFilter &&
          filterLabel !== removedFilter
        );
      }
      return true;
    });

    // Update state with new array to trigger reactivity
    state.filters.values = [...updatedFilters];

    filter.setFilters({
      key: state.filters.key,
      filters: updatedFilters,
    });

    state.filters.clearSelection = filterToRemove;
    // Reset to first page when filter is removed
    state.pagination.page = 1;
  };

  const clearFilters = async () => {
    state.filters.values = [];
    await applyFilters(state.filters.values);
  };

  const clearSearch = async () => {
    state.filters.searchText = "";
  };

  // Search Operations
  const debounce = (func, delay) => {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), delay);
    };
  };

  const performSearch = async (string) => {
    // Always clear existing search filters first
    positions.removePositionFilter("name");

    // Only add search filters and load positions if there's actually a search term
    if (string && string.trim() !== "") {
      positions.addPositionFilter("name", string);
    }
    positions.setPaginationDataOffset(0);
    state.filters.searchText = string;
    state.pagination.page = 1;
    await positions.loadAllPositions();
  };

  const debouncedSearch = debounce(performSearch, 500);

  const handleSearch = (string) => {
    const queryString = string;
    debouncedSearch(queryString);
  };

  // Pagination and Sorting
  const handleRequest = async ({ page, itemsPerPage }) => {
    // Handle frontend pagination
    if (page) {
      state.pagination.page = page;
    }
    if (itemsPerPage) {
      state.pagination.perPage = itemsPerPage;
    }
  };

  const handleSort = async (sortData) => {
    // Handle frontend sorting
    if (sortData) {
      state.sortField = sortData.field || "position_name";
      state.sortDescending = sortData.descending || false;
      // Reset to first page when sorting changes
      state.pagination.page = 1;
    }
  };

  // Position Operations
  const handlePositionDropdownOperation = async (operation, position) => {
    if (!operation || !position) return;

    positions.setCurrentPosition(position);
    state.currentPosition = position;

    switch (operation.value) {
      case "activate_action":
        state.activatePositionModal.show = true;
        break;
      case "delete_action":
        state.deletePositionModal.show = true;
        break;
      case "rename_action":
        state.renamePositionModal.show = true;
        break;
      case "attach_position_role":
        await prepareAttachRole();
        state.openDefault = true;
        break;
      case "archive_action":
        state.archiveAction = "archive";
        state.archivePositionModal.show = true;
        break;
      case "unarchive_action":
        state.archiveAction = "unarchive";
        state.archivePositionModal.show = true;
        break;
      case "retire_action":
        state.retirePositionModal.show = true;
        break;
      case "reactivate_action":
        state.reactivatePositionModal.show = true;
        break;
      case "deactivate_action":
        state.deactivatePositionModal.show = true;
        break;
      case "cloned_action":
        state.clonePositionModal.show = true;
        break;
    }
  };

  // Position Operation Handler - same as usePositionViewState
  const handlePositionOperation = async (
    operation,
    accountId,
    payload = null
  ) => {
    try {
      proxy?.$loader?.setAppLoading(true);
      let newPosition;
      let clonedPosition;

      switch (operation) {
        case positions.operations.CREATE_POSITION:
          if (!payload) return;
          newPosition = await positions.createPosition({
            accountId,
            name: payload,
          });
          
          if (newPosition?.id) {
            state.addPositionModal.show = false;
            proxy?.$loader?.setAppLoading(false);
            await nextTick();
            
            // Navigate to position detail page using the same route as OrganizationPositionsTab
            proxy?.$router?.push({
              name: 'PositionDetailChild',
              params: { positionId: newPosition.id },
              query: { positionTab: 'purpose' }
            }).catch(() => {});
            return;
          }
          
          state.addPositionModal.show = false;
          break;

        case positions.operations.ACTIVATE_POSITION:
          await positions.activate({
            accountId,
            position: positions.getCurrentPosition(),
          });
          state.activatePositionModal.show = false;
          await positions.loadAllPositions();
          break;

        case positions.operations.DELETE_POSITION:
          await positions.deletePosition({
            accountId,
            position: positions.getCurrentPosition(),
          });
          state.deletePositionModal.show = false;
          await positions.loadAllPositions();
          break;

        case positions.operations.RENAME_POSITION:
          if (!payload) return;
          await positions.rename({
            accountId,
            position: positions.getCurrentPosition(),
            newName: payload,
          });
          state.renamePositionModal.show = false;
          await positions.loadAllPositions();
          break;

        case positions.operations.ARCHIVE_POSITION:
          if (state.archiveAction === "archive") {
            await positions.archive({
              accountId,
              position: positions.getCurrentPosition(),
            });
          } else if (state.archiveAction === "unarchive") {
            await positions.unarchive({
              accountId,
              position: positions.getCurrentPosition(),
            });
          }
          state.archivePositionModal.show = false;
          await positions.loadAllPositions();
          break;

        case positions.operations.RETIRE_POSITION:
          await positions.retire({
            accountId,
            position: positions.getCurrentPosition(),
          });
          state.retirePositionModal.show = false;
          await positions.loadAllPositions();
          break;

        case positions.operations.REACTIVATE_POSITION:
          await positions.reactivate({
            accountId,
            position: positions.getCurrentPosition(),
          });
          state.reactivatePositionModal.show = false;
          await positions.loadAllPositions();
          break;

        case positions.operations.DEACTIVATE_POSITION:
          await positions.deactivate({
            accountId,
            position: positions.getCurrentPosition(),
          });
          state.deactivatePositionModal.show = false;
          await positions.loadAllPositions();
          break;

        case positions.operations.CLONE_POSITION: {
          clonedPosition = await positions.clonePosition({
            accountId,
            position: positions.getCurrentPosition(),
          });
          const idPosition = positions.getCurrentPosition().id;
          let rolesAttached = await positions.getRolesAttachedToPosition(
            idPosition
          );
          for (const element of rolesAttached) {
            await positions.attachRole({
              accountId,
              positionId: clonedPosition.id,
              roleId: element.id,
            });
          }
          state.clonePositionModal.show = false;
          state.filters.searchText = clonedPosition.code_str;
          performSearch(clonedPosition.code_str);
          setTimeout(() => {
            highlight({ id: clonedPosition.id, type: "Positions" });
          }, 1000);
          await roles.loadAllRoles();
          break;
        }
      }
      positions.setCurrentPosition(null);
    } catch (error) {
      console.error("Error in position operation:", error);
      if (error.status === "400") {
        state.errorPopup.title = "Operation Failed";
        state.errorPopup.message =
          error.response?.data.join(" ") || "Operation failed";
        state.errorPopup.show = true;
      }
    } finally {
      proxy?.$loader?.setAppLoading(false);
    }
  };

  // Attach Role Methods
  const prepareAttachRole = async () => {
    const currentPosition = positions.getCurrentPosition();
    if (!currentPosition?.id) return;
    state.attachRoleModal.currentPositionId = currentPosition.id;
    await getListOfRoles(currentPosition.id, 1, "", false);
    state.attachRoleModal.show = true;
  };

  const getListOfRoles = async (
    positionId,
    page = 1,
    searchTerm = "",
    isLoadMore = false
  ) => {
    const limit = 10;
    const offset = (page - 1) * limit;
    const name = searchTerm || "";
    const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
    try {
      if (isLoadMore) {
        state.attachRoleModal.isLoadingMore = true;
      }

      const attachableeRoles = await getRolesListAvailableForPosition(
        accountId,
        positionId,
        limit,
        offset,
        name
      );

      const newItems = attachableeRoles.data.results.map((item) => ({
        id: item.id,
        code: item.code_str.match(/\d+/)[0],
        name: item.name,
        status: item.status,
        attached: item?.attached_to_position,
      }));

      if (isLoadMore && state.attachRoleModal.roles) {
        state.attachRoleModal.roles = [
          ...state.attachRoleModal.roles,
          ...newItems,
        ];
      } else {
        state.attachRoleModal.roles = newItems;
      }

      state.attachRoleModal.page = page;
      state.attachRoleModal.totalCount = attachableeRoles.data.count || 0;
      state.attachRoleModal.hasMoreData =
        page * limit < state.attachRoleModal.totalCount;
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      if (isLoadMore) {
        state.attachRoleModal.isLoadingMore = false;
      }
    }
  };

  const loadMoreRoles = async () => {
    if (
      state.attachRoleModal.isLoadingMore ||
      !state.attachRoleModal.hasMoreData
    ) {
      return;
    }
    const nextPage = state.attachRoleModal.page + 1;
    await getListOfRoles(
      state.attachRoleModal.currentPositionId,
      nextPage,
      state.attachRoleModal.currentSearchTerm,
      true
    );
  };

  const searchRoles = async (searchTerm) => {
    state.attachRoleModal.currentSearchTerm = searchTerm;
    state.attachRoleModal.page = 1;
    state.attachRoleModal.hasMoreData = true;
    await getListOfRoles(
      state.attachRoleModal.currentPositionId,
      1,
      searchTerm,
      false
    );
  };

  const handleRoleSelected = (role) => {
    if (role?.id) {
      state.attachRoleModal.selectedRoleId = role.id;
    }
  };

  const handleAddRoleSelected = async (data) => {
    proxy?.$loader?.show();
    try {
      const accountId = JSON.parse(localStorage.getItem("currentAccountID"));
      const newRole = await roles.saveNewRole({
        accountId: accountId,
        roleName: data.name,
      });
      if (newRole) {
        state.attachRoleModal.selectedRoleId = newRole.id;
      }
      await attachRole(accountId);
    } finally {
      proxy?.$loader?.hide();
    }
  };

  const attachRole = async (accountId) => {
    if (
      !state.attachRoleModal.selectedRoleId ||
      !positions.getCurrentPosition()?.id
    )
      return;
    try {
      proxy?.$loader?.show();
      await positions.attachRole({
        accountId,
        positionId: positions.getCurrentPosition().id,
        roleId: state.attachRoleModal.selectedRoleId,
      });
      state.attachRoleModal.show = false;
      await positions.loadAllPositions();
      await roles.loadAllRoles();
    } catch (e) {
      console.error(e);
    } finally {
      proxy?.$loader?.hide();
    }
  };

  // Relieve Assignment Methods
  const getRelieveAssignmentAvatar = () => {
    const employee = state.relieveAssignment.assignedEmployee;
    if (!employee) return null;

    const employeeName =
      employee.assigned_employee_name || employee.employeeName || "";
    const nameParts = employeeName.split(" ");

    return {
      picture: getEmployeeAvatar(employee),
      firstName: nameParts[0] || "U",
      lastName: nameParts[1] || "S",
    };
  };

  const closeRelieveAssignment = () => {
    state.relieveAssignment.show = false;
    state.relieveAssignment.currentPosition = null;
    state.relieveAssignment.assignedEmployee = null;
  };

  const relievePrimaryAssignment = async (accountId) => {
    const position = state.relieveAssignment.currentPosition;
    if (state.relieveAssignment.type === "primary") {
      if (!position?.assigned_primary) return;
      proxy?.$loader?.setAppLoading(true);
      try {
        const idEmployee =
          position.assigned_primary.assigned_employee_id || null;
        const employeeData = await getSingleEmployeeV2(accountId, idEmployee);
        const assignmentId = employeeData?.data?.primary_position?.id || null;
        const employeeId = employeeData?.data?.id || null;

        await positions.relievePrimaryAssignment({ accountId, assignmentId });
        await positions.loadAllPositions();
        await recalculateEmployeeReadiness(accountId, employeeId);
        closeRelieveAssignment();
      } catch (error) {
        console.error("Error relieving primary assignment:", error);
      } finally {
        proxy?.$loader?.setAppLoading(false);
      }
    } else if (state.relieveAssignment.type === "prepare") {
      if (!position?.assigned_preparing_for) return;
      proxy?.$loader?.setAppLoading(true);
      try {
        const idEmployee =
          state.relieveAssignment?.assignedEmployee?.assigned_employee_id ||
          null;
        const employeeData = await getSingleEmployeeV2(accountId, idEmployee);
        const assignedPosition =
          employeeData?.data?.preparing_for_positions.find(
            (el) => el.code_str === position.code_str
          );
        const assignmentId = assignedPosition?.id;
        const employeeId = employeeData?.data?.id || null;
        await positions.relievePrimaryAssignment({
          accountId,
          assignmentId,
          employeeId,
        });
        await positions.loadAllPositions();
        await recalculateEmployeeReadiness(accountId, employeeId);
        closeRelieveAssignment();
      } catch (error) {
        console.error("Error relieving primary assignment:", error);
      } finally {
        proxy?.$loader?.setAppLoading(false);
      }
    }
  };

  // Assignment Management
  const onRelievePrimaryAssignment = (position) => {
    // This will be handled by parent component via event emission
    // Store position for potential future use
    state.currentPosition = position;
  };

  const onRelievePrepareAssignment = (position) => {
    // This will be handled by parent component via event emission
    // Store position for potential future use
    state.currentPosition = position;
  };

  const onPreparingAvatarsClick = (position) => {
    // This will be handled by parent component via event emission
    state.currentPosition = position;
  };

  const onPreparingEffectiveDateClick = (event, position) => {
    // This will be handled by parent component via event emission
    state.currentPosition = position;
  };

  // UI Helpers
  const getStatusColor = (status) => {
    const colorMap = {
      active: "#10B981",
      draft: "#667085",
      retired: "#F79009",
      inactive: "#EF4444",
    };
    return colorMap[status] || "#667085";
  };

  const openPositionDetails = async (position) => {
    if (position?.id) {
      state.currentPosition = position;
      // Navigate to position details or emit event
      try {
        if (proxy?.$router) {
          await proxy.$router.push(
            `/new-organization/positions/position/${position.id}`
          );
        }
      } catch (e) {
        // In Storybook or other contexts, router might not be available
        console.warn("Router not available:", e);
      }
    }
  };

  // Chatter Operations
  const getPositionChatter = async () => {
    try {
      const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
      state.openChatterModal.show = true;
      const response = await getChatterList(
        accountID,
        state.openChatterModal.positionId,
        "position",
        state.openChatterModal.page
      );
      if (response) {
        let chatterItems = response.data.results.map((el) => {
          const date = new Date(el.recorded_on);
          const milliseconds = date.getTime();
          const millisecondsString = milliseconds.toString();
          let nameParts = el.recorded_by_name.split(" ");
          const newEl = {};
          newEl.id = el.id;
          if (nameParts.length === 2) {
            newEl.avatar = {
              picture: "",
              firstName: nameParts[0],
              lastName: nameParts[1],
            };
          } else {
            newEl.avatar = {
              picture: "",
              firstName: el.recorded_by_name,
              lastName: "",
            };
          }
          newEl.IsExIQtiveBot = false;
          newEl.message = el.note;
          newEl.created_on = millisecondsString;
          newEl.IsHistory = el.is_history;
          return newEl;
        });
        state.openChatterModal.chatterData.push(...chatterItems);
        state.openChatterModal.totalPositionChatter = response.data.count;
      }
    } catch (error) {
      console.log("e: ", error);
    }
  };

  const openPositionChatterModal = async (item) => {
    positions.setCurrentPosition(item);
    state.currentPosition = item;
    state.openChatterModal.chatterData = [];
    state.openChatterModal.positionId = item?.id;
    await getPositionChatter();
  };

  const closePositionChatterModal = () => {
    state.openChatterModal.chatterData = [];
    state.openChatterModal.totalPositionChatter = 0;
    state.openChatterModal.page = 1;
    state.openChatterModal.show = false;
  };

  const requestMoreChatter = () => {
    const page = Math.ceil(state.openChatterModal.chatterData.length / 10);
    state.openChatterModal.page = page + 1;
    getPositionChatter();
  };

  // Initialize filters from persistence
  const getFilters = () => {
    const persistedFilters = filter.getFilters(state.filters.key);
    if (persistedFilters) {
      state.filters.values = persistedFilters;
    }
  };
  const initializeFilters = async () => {
    const app = getCurrentInstance();
    const { proxy } = app || {};

    // Check feature flag for hiding archived filter
    const hideArchivedFilterPositions = await proxy.$getFlag(
      "hide_archived_filter_positions",
      false
    );

    if (hideArchivedFilterPositions) {
      // Remove archived filter from filterGroups
      state.filters.filterGroups = state.filters.filterGroups.filter(
        (group) => group.label !== "Archived"
      );
    }
  };

  onMounted(async () => {
    await initializeFilters();
    getFilters();
    // applyFilters(
    //   state.filters.values.map((value) => ({ label: value, value: value }))
    // );
  });

  instance = {
    state,
    config: state.config,
    applyFilters,
    removeFilters,
    clearFilters,
    clearSearch,
    handleSearch,
    handleRequest,
    handleSort,
    handlePositionDropdownOperation,
    handlePositionOperation,
    onRelievePrimaryAssignment,
    onRelievePrepareAssignment,
    onPreparingAvatarsClick,
    onPreparingEffectiveDateClick,
    getStatusColor,
    openPositionDetails,
    openPositionChatterModal,
    closePositionChatterModal,
    requestMoreChatter,
    getFilters,
    // Attach Role methods
    prepareAttachRole,
    getListOfRoles,
    loadMoreRoles,
    searchRoles,
    handleRoleSelected,
    handleAddRoleSelected,
    attachRole,
    // Relieve Assignment methods
    getRelieveAssignmentAvatar,
    closeRelieveAssignment,
    relievePrimaryAssignment,
    // Positions reference
    positions,
  };

  return instance;
}

export function resetOrganizationPositionsViewState() {
  instance = null;
}
